/**
 * Oatmeal Minimal Quick Add — Option C (Hybrid)
 * Shows only: Title, Price, Color, Size, Add to Cart
 * Uses theme’s real <product-form-component> so Shopify cart updates normally
 */

(function () {
  'use strict';

  const prefetchedQuickAddContent = new WeakMap();
  const prefetchPromises = new WeakMap();

  function getCacheBucket(store, key) {
    let bucket = store.get(key);
    if (!bucket) {
      bucket = new Map();
      store.set(key, bucket);
    }
    return bucket;
  }

  function getQuickAddDialog() {
    return document.getElementById('quick-add-dialog');
  }

  function getQuickAddIconSvg(name) {
    return document.querySelector(`[data-oat-qa-icon-source] [data-oat-qa-icon="${name}"]`)?.innerHTML?.trim() || '';
  }

  function normalizeAvailabilityMessage(message) {
    if (typeof window.OatmealNormalizeAvailabilityMessage === 'function') {
      return window.OatmealNormalizeAvailabilityMessage(message);
    }

    const raw = String(message || '').trim();
    const lower = raw.toLowerCase();
    if (
      lower.includes('sold out') ||
      lower.includes('out of stock') ||
      lower.includes('unavailable') ||
      lower.includes('inventory') ||
      lower.includes('available quantity') ||
      lower.includes('maximum')
    ) {
      return 'This size or colour is not available right now.';
    }
    return raw || 'Could not add this item to your bowl.';
  }

  function showQuickAddToast(modalContent, message) {
    const dialog = getQuickAddDialog();
    const host = dialog?.querySelector('.dialog__inner') || dialog || modalContent || document.body;

    if (typeof window.OatmealShowToast === 'function') {
      window.OatmealShowToast(normalizeAvailabilityMessage(message), host);
      return;
    }

    let toast = host.querySelector('[data-oatmeal-toast]');
    if (!(toast instanceof HTMLElement)) {
      toast = document.createElement('div');
      toast.setAttribute('data-oatmeal-toast', '');
      toast.style.position = host === document.body ? 'fixed' : 'absolute';
      toast.style.left = '50%';
      toast.style.bottom = '24px';
      toast.style.transform = 'translateX(-50%)';
      toast.style.zIndex = '9999';
      toast.style.pointerEvents = 'none';
      toast.style.padding = '10px 14px';
      toast.style.borderRadius = '999px';
      toast.style.background = '#76232B';
      toast.style.color = '#F1EDE4';
      toast.style.fontSize = '12px';
      toast.style.lineHeight = '1.2';
      toast.style.maxWidth = 'calc(100% - 32px)';
      toast.style.textAlign = 'center';
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 240ms ease';

      if (host !== document.body && getComputedStyle(host).position === 'static') {
        host.style.position = 'relative';
      }

      host.appendChild(toast);
    }

    toast.textContent = normalizeAvailabilityMessage(message);
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });
    clearTimeout(toast.__oatmealTimer);
    toast.__oatmealTimer = setTimeout(() => {
      toast.style.opacity = '0';
    }, 3200);
  }

  function getSelectedVariantIdFromCard(card) {
    if (!card) return null;

    const checkedInput = card.querySelector('input[type="radio"]:checked[data-variant-id]');
    const checkedVariantId = checkedInput?.dataset?.variantId;
    if (checkedVariantId) return String(checkedVariantId);

    const linkedVariantId = card.dataset.oatSelectedVariantId;
    if (linkedVariantId) return String(linkedVariantId);

    return null;
  }

  function patchQuickAdd() {
    const QuickAddComponent = customElements.get('quick-add-component');

    if (!QuickAddComponent) {
      setTimeout(patchQuickAdd, 50);
      return;
    }

    QuickAddComponent.prototype.updateQuickAddModal = async function (productGrid) {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (!modalContent || !productGrid) return;

      const productData = extractMinimalProductData.call(this, productGrid);
      const minimalHTML = buildMinimalOverlay(productData);

      // Render minimal UI
      modalContent.innerHTML = minimalHTML;

      // Inject REAL theme product form (required for cart drawer/count/events)
      const realProductFormComponent = productGrid.querySelector('product-form-component');
      if (realProductFormComponent) {
        const cloned = realProductFormComponent.cloneNode(true);
        // keep it hidden but in-DOM so theme JS hooks work
        if (cloned instanceof HTMLElement) cloned.style.display = 'none';
        modalContent.appendChild(cloned);
      } else {
        console.warn('[Oatmeal QA] product-form-component not found in fetched product grid');
      }

      attachMinimalEventListeners.call(this, modalContent);
    };

    QuickAddComponent.prototype.oatPrefetchQuickAdd = function () {
      const currentUrl = this.productPageUrl;
      if (!currentUrl) return Promise.resolve(null);

      const cachedContent = getCacheBucket(prefetchedQuickAddContent, this);
      if (cachedContent.has(currentUrl)) {
        return Promise.resolve(cachedContent.get(currentUrl));
      }

      const inFlight = getCacheBucket(prefetchPromises, this);
      if (inFlight.has(currentUrl)) {
        return inFlight.get(currentUrl);
      }

      const request = this.fetchProductPage(currentUrl)
        .then((html) => {
          const gridElement = html?.querySelector('[data-product-grid-content]');
          if (!gridElement) return null;

          const clonedGrid = gridElement.cloneNode(true);
          cachedContent.set(currentUrl, clonedGrid);
          return clonedGrid;
        })
        .catch((error) => {
          console.error('[Oatmeal QA] Quick add prefetch failed', error);
          return null;
        })
        .finally(() => {
          inFlight.delete(currentUrl);
        });

      inFlight.set(currentUrl, request);
      return request;
    };

    QuickAddComponent.prototype.oatHasPrefetchedQuickAdd = function () {
      const currentUrl = this.productPageUrl;
      if (!currentUrl) return false;
      return getCacheBucket(prefetchedQuickAddContent, this).has(currentUrl);
    };

    QuickAddComponent.prototype.oatBuildLocalQuickAdd = function () {
      const productCard = this.closest('product-card');
      if (!productCard) return null;

      const localGrid = document.createElement('div');
      localGrid.setAttribute('data-product-grid-content', '');

      const productPrice = productCard.querySelector('product-price');
      const variantPicker = productCard.querySelector(
        'variant-picker, swatches-variant-picker-component, variant-radios, variant-selects, [data-variant-picker]'
      );
      const productFormComponent = this.querySelector('product-form-component');

      if (!productFormComponent) return null;

      if (productPrice) {
        localGrid.appendChild(productPrice.cloneNode(true));
      }

      if (variantPicker) {
        localGrid.appendChild(variantPicker.cloneNode(true));
      }

      localGrid.appendChild(productFormComponent.cloneNode(true));
      return localGrid;
    };

    QuickAddComponent.prototype.oatCanUseLocalQuickAdd = function (productGrid) {
      if (!productGrid) return false;

      const data = extractMinimalProductData.call(this, productGrid);
      const hasSizes = Array.isArray(data.sizeOptions) && data.sizeOptions.length > 0;
      return hasSizes;
    };

    QuickAddComponent.prototype.oatOpenQuickAdd = async function () {
      const currentUrl = this.productPageUrl;
      if (!currentUrl) return;

      const cachedContent = getCacheBucket(prefetchedQuickAddContent, this);
      const localGrid = this.oatBuildLocalQuickAdd?.() || null;
      let productGrid = null;

      if (localGrid && this.oatCanUseLocalQuickAdd?.(localGrid)) {
        productGrid = localGrid;
      } else {
        productGrid = cachedContent.get(currentUrl) || null;
      }

      if (!productGrid) {
        productGrid = await this.oatPrefetchQuickAdd();
      }

      if (productGrid) {
        await this.updateQuickAddModal(productGrid.cloneNode(true));
      }

      const dialogComponent = getQuickAddDialog();
      if (dialogComponent && typeof dialogComponent.showDialog === 'function') {
        dialogComponent.showDialog();
      }
    };
  }

  function extractMinimalProductData(productGrid) {
    const variantPicker =
      productGrid.querySelector('variant-picker, variant-radios, variant-selects, [data-variant-picker]') ||
      productGrid;

    const productPrice =
      productGrid.querySelector('product-price') ||
      productGrid.querySelector('.price');

    const colorOptions = extractVariantOptions(variantPicker, 'Color');
    const sizeOptions = extractVariantOptions(variantPicker, 'Size');
    const productId =
      this.dataset.productId ||
      this.dataset.currentProductId ||
      productGrid.dataset.productId ||
      variantPicker?.dataset?.productId ||
      '';
    const productHandle = (() => {
      const raw = this.productPageUrl || '';
      if (!raw) return '';
      try {
        const parsed = new URL(raw, window.location.origin);
        const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      } catch (e) {
        const match = raw.match(/\/products\/([^/?#]+)/);
        return match ? decodeURIComponent(match[1]) : '';
      }
    })();

    let selectedVariantId =
      this.dataset.selectedVariantId ||
      this.dataset.variantId ||
      this.dataset.currentVariantId ||
      productGrid.dataset.selectedVariantId ||
      productGrid.dataset.variantId ||
      null;

    if (!selectedVariantId && this.productPageUrl) {
      try {
        const u = new URL(this.productPageUrl, window.location.origin);
        const vid = u.searchParams.get('variant');
        if (vid) selectedVariantId = vid;
      } catch (e) {}
    }

    if (selectedVariantId) {
      const idStr = String(selectedVariantId);
      colorOptions.forEach(opt => {
        if (opt.variantId && String(opt.variantId) === idStr) opt.checked = true;
      });
      sizeOptions.forEach(opt => {
        if (opt.variantId && String(opt.variantId) === idStr) opt.checked = true;
      });
    }

    return {
      productId,
      productHandle,
      productUrl: this.productPageUrl,
      productTitle: this.dataset.productTitle || '',
      price:
        productPrice?.querySelector('.price-item--regular')?.textContent?.trim() ||
        productPrice?.textContent?.trim() ||
        '',
      colorOptions,
      sizeOptions,
    };
  }

  function extractVariantOptions(rootEl, optionName) {
    if (!rootEl) return [];

    const key = optionName.toLowerCase();

    const fieldsets = Array.from(rootEl.querySelectorAll('fieldset'));
    const targetFieldset = fieldsets.find(fs => {
      const legend = fs.querySelector('legend');
      if (!legend) return false;
      const text = legend.textContent.trim().toLowerCase();
      return text === key || text.includes(key);
    });

    let inputs;

    if (targetFieldset) {
      inputs = Array.from(targetFieldset.querySelectorAll('input[type="radio"]'));
    } else {
      const allRadios = Array.from(rootEl.querySelectorAll('input[type="radio"]'));
      inputs = allRadios.filter(input => (input.name || '').toLowerCase().includes(key));
    }

    if (!inputs.length) return [];

    return inputs.map(input => {
      const label = input.closest('label') || input.parentElement;

      let swatchStyle = '';
      let swatchImage = '';

      if (key === 'color') {
        const swatchEl =
          label?.querySelector('.swatch') ||
          label?.querySelector('[class*="swatch"]') ||
          label?.querySelector('[style*="--swatch-background"]') ||
          label?.querySelector('[style*="background"]');

        if (swatchEl) {
          swatchStyle = swatchEl.getAttribute('style') || swatchEl.style.cssText || '';
          const img = swatchEl.querySelector('img');
          if (img) swatchImage = img.src;
        }
      }

      const ariaSelected =
        input.getAttribute('aria-checked') === 'true' ||
        input.getAttribute('aria-selected') === 'true' ||
        input.getAttribute('aria-pressed') === 'true';

      const checked = !!(input.checked || ariaSelected);

      return {
        value: input.value,
        label: input.dataset.optionValue || input.value,
        variantId: input.dataset.variantId,
        available: !input.disabled,
        checked,
        swatchStyle,
        swatchImage,
      };
    });
  }

  function getColorDisplayName(colorName) {
    const raw = String(colorName || '').trim();
    const lower = raw.toLowerCase();

    if (lower.includes('plum perfect')) return 'Plum';
    if (lower.includes('lily pad')) return 'Guacamole';
    if (lower.includes('sun dried tomato')) return 'Salsa Sauce';
    if (lower.includes('black')) return 'Black Pepper';
    if (lower.includes('dress blue')) return 'Blueberry';
    if (lower.includes('white')) return 'Milk';

    return raw;
  }

  function getAddToCartSVG() {
    const el = document.getElementById('oatmeal-svg-add-to-cart');
    return el ? el.innerHTML.trim() : '';
  }

  function buildMinimalOverlay(data) {
    const selectedColor = data.colorOptions.find(opt => opt.checked) || data.colorOptions[0];
    const selectedColorDisplay = getColorDisplayName(selectedColor?.label || '');
    const selectedColorValue = selectedColor?.value || selectedColor?.label || '';
    const selectedVariantId =
      data.sizeOptions.find(opt => opt.checked)?.variantId ||
      selectedColor?.variantId ||
      '';
    const favIcon = getQuickAddIconSvg('fav');
    const favSelectedIcon = getQuickAddIconSvg('fav-selected');
    const shareIcon = getQuickAddIconSvg('share');

    return `
      <div class="oat-quick-add oat-quick-add--pdp-mini">
        <div class="oat-qa-topbar">
          <div class="oat-qa-intro">
            <span class="oat-qa-intro__line">Someone's got a brilliant taste!</span>
            <span class="oat-qa-intro__line">Add this ingredient to your bowl now to make it yummier!!!</span>
          </div>
        </div>
        <div class="oat-qa-header-top">
          <h2 class="product__title h4 oat-qa-title">${data.productTitle}</h2>
        </div>

        <div class="oat-qa-price-row">
          <div class="price oat-qa-price">
            <span class="price-item price-item--regular">${data.price}</span>
          </div>
        </div>

        <div class="oat-qa-body">
          <div class="product-form__input oat-qa-field oat-qa-field--color">
            <div class="form__label oat-qa-field-label">
              <span>Color</span>
              <span class="oat-qa-selected-value">${selectedColorDisplay}</span>
            </div>
            <div class="oat-qa-color-row">
              <div class="oat-qa-color-swatches">
                ${data.colorOptions.map((opt, index) => renderColorSwatch(opt, index)).join('')}
              </div>
              <div class="oat-qa-color-actions" aria-label="Quick actions">
                <button
                  type="button"
                  class="oat-qa-icon-btn oat-qa-icon-btn--fav"
                  data-oat-qa-fav
                  data-product-id="${data.productId || ''}"
                  data-product-handle="${data.productHandle || ''}"
                  data-product-url="${data.productUrl || ''}"
                  data-variant-id="${selectedVariantId}"
                  data-color="${selectedColorValue}"
                  aria-label="Add to wishlist"
                  aria-pressed="false"
                >
                  <span class="oat-qa-icon-wrap oat-qa-icon-wrap--fav" aria-hidden="true">
                    ${favIcon}
                    ${favSelectedIcon}
                  </span>
                </button>
                <button
                  type="button"
                  class="oat-qa-icon-btn oat-qa-icon-btn--share"
                  data-oat-qa-share
                  data-product-url="${data.productUrl || ''}"
                  data-variant-id="${selectedVariantId}"
                  aria-label="Share product"
                >
                  <span class="oat-qa-icon-wrap oat-qa-icon-wrap--share" aria-hidden="true">
                    ${shareIcon}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <div class="product-form__input oat-qa-field oat-qa-field--size">
            <div class="form__label oat-qa-field-label">
              <span>Please select size</span>
              <a
                href="${data.productUrl}#size-chart"
                class="variant-option__size-chart link oat-qa-size-chart"
                data-oat-size-chart
                data-size-chart-img="${window.OAT_SIZE_CHART_IMG || ''}"
              >Size chart</a>
            </div>

            <div class="oat-qa-size-buttons variant-option--equal-width-buttons">
              ${data.sizeOptions
                .map(
                  opt => `
                <label class="variant-option__button-label">
                  <input
                    type="radio"
                    name="oat-quick-add-size"
                    value="${opt.value}"
                    aria-label="${opt.label}"
                    data-variant-id="${opt.variantId || ''}"
                    ${opt.available ? '' : 'disabled'}
                  >
                  <span class="variant-option__button-label__pill"></span>
                  <span class="variant-option__button-label__text">${opt.label}</span>
                </label>
              `
                )
                .join('')}
            </div>
          </div>
        </div>

        <footer class="oat-qa-footer">
          <div class="oat-qa-actions">
            <div class="oat-qa-quantity" aria-label="Quantity selector">
              <button
                type="button"
                class="oat-qa-quantity__btn"
                data-oat-qty-decrease
                aria-label="Decrease quantity"
              >−</button>
              <span class="oat-qa-quantity__value" data-oat-qty-value>1</span>
              <button
                type="button"
                class="oat-qa-quantity__btn"
                data-oat-qty-increase
                aria-label="Increase quantity"
              >+</button>
            </div>

            <button
              type="button"
              class="oat-qa-add-btn product-form__submit button button--primary"
              data-add-to-cart
              disabled
            >
              <span class="oat-qa-add-icon">${getAddToCartSVG()}</span>
              <span class="oat-qa-add-btn-text">Add to Bowl</span>
            </button>
          </div>

          <a href="${data.productUrl}" class="oat-qa-view-details underlined-link">
            View full details →
          </a>
        </footer>
      </div>
    `;
  }

  function renderColorSwatch(option, index) {
    const inputName = 'oat-qa-color';
    const inputId = `oat-qa-color-${index}`;
    const checkedAttr = option.checked ? 'checked' : '';
    const displayLabel = getColorDisplayName(option.label);

    let swatchInner = '';

    if (option.swatchImage) {
      swatchInner = `<img src="${option.swatchImage}" alt="${displayLabel}">`;
    } else if (option.swatchStyle) {
      swatchInner = `<span class="swatch" style="${option.swatchStyle}"></span>`;
    } else {
      const color = getColorFallback(option.label);
      swatchInner = `<span class="swatch" style="--swatch-background:${color};background-color:${color};"></span>`;
    }

    const selectedClasses = option.checked ? 'selected is-selected' : '';

    return `
      <button
        type="button"
        class="
          oat-qa-color-swatch
          variant-option__button-label
          variant-option__button-label--has-swatch
          ${selectedClasses}
          ${!option.available ? 'is-unavailable' : ''}
        "
        data-color="${option.value}"
        data-variant-id="${option.variantId || ''}"
        aria-label="${displayLabel}"
        title="${displayLabel}"
        ${!option.available ? 'disabled' : ''}
      >
        <input
          type="radio"
          class="oat-qa-color-input"
          name="${inputName}"
          id="${inputId}"
          value="${option.value}"
          ${checkedAttr}
          tabindex="-1"
          aria-hidden="true"
        >
        ${swatchInner}
      </button>
    `;
  }

  function getColorFallback(colorName) {
    const colors = {
      'Plum Perfect': '#8B4789',
      'Lily Pad': '#90B494',
      'Sun Dried Tomato': '#C85A54',
      Black: '#000000',
      'Dress Blue': '#2C3E50',
    };
    return colors[colorName] || '#CCCCCC';
  }

  function attachMinimalEventListeners(modalContent) {
    const colorButtons = modalContent.querySelectorAll('.oat-qa-color-swatch');
    colorButtons.forEach(btn => btn.addEventListener('click', () => handleColorChange(btn, modalContent)));

    const sizeInputs = modalContent.querySelectorAll('.oat-qa-size-buttons input[type="radio"]');
    sizeInputs.forEach(input => input.addEventListener('change', () => {
      updateAddButton(modalContent);
      updateUtilityActions(modalContent);
    }));

    const addBtn = modalContent.querySelector('[data-add-to-cart]');
    addBtn?.addEventListener('click', () => handleAddToCart(modalContent));

    const decreaseBtn = modalContent.querySelector('[data-oat-qty-decrease]');
    const increaseBtn = modalContent.querySelector('[data-oat-qty-increase]');
    decreaseBtn?.addEventListener('click', () => updateQuantity(modalContent, -1));
    increaseBtn?.addEventListener('click', () => updateQuantity(modalContent, 1));

    const favBtn = modalContent.querySelector('[data-oat-qa-fav]');
    favBtn?.addEventListener('click', () => toggleQuickAddWishlist(modalContent));

    const shareBtn = modalContent.querySelector('[data-oat-qa-share]');
    shareBtn?.addEventListener('click', () => handleQuickAddShare(modalContent));

    syncQuantityToForm(modalContent, 1);

    updateAddButton(modalContent);
    updateUtilityActions(modalContent);

    const sizeChartLink = modalContent.querySelector('[data-oat-size-chart]');
    sizeChartLink?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const imgUrl = sizeChartLink.dataset.sizeChartImg || window.OAT_SIZE_CHART_IMG || '';
      const opened = openSizeChartDialog(imgUrl);
      if (!opened) window.location.href = sizeChartLink.getAttribute('href');
    });
  }

  function handleColorChange(button, modalContent) {
    if (button.disabled) return;

    const allButtons = modalContent.querySelectorAll('.oat-qa-color-swatch');
    const allRadios = modalContent.querySelectorAll('.oat-qa-color-input');

    allButtons.forEach(btn => btn.classList.remove('selected', 'is-selected'));
    allRadios.forEach(r => {
      r.checked = false;
      r.removeAttribute('checked');
    });

    button.classList.add('selected', 'is-selected');
    const radio = button.querySelector('.oat-qa-color-input');
    if (radio) {
      radio.checked = true;
      radio.setAttribute('checked', 'checked');
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const label = modalContent.querySelector('.oat-qa-field-label .oat-qa-selected-value');
    if (label) label.textContent = button.getAttribute('aria-label') || '';

    const sizeInputs = modalContent.querySelectorAll('.oat-qa-size-buttons input[type="radio"]');
    sizeInputs.forEach(input => (input.checked = false));

    updateAddButton(modalContent);
    updateUtilityActions(modalContent);
  }

  function getSelectedVariantId(modalContent) {
    return (
      modalContent.querySelector('.oat-qa-size-buttons input[type="radio"]:checked')?.dataset.variantId ||
      modalContent.querySelector('.oat-qa-color-swatch.selected .oat-qa-color-input')?.dataset.variantId ||
      ''
    );
  }

  function getShareUrl(modalContent) {
    const baseUrl =
      modalContent.querySelector('[data-oat-qa-share]')?.getAttribute('data-product-url') ||
      window.location.href;
    const variantId = getSelectedVariantId(modalContent);

    try {
      const url = new URL(baseUrl, window.location.origin);
      if (variantId) url.searchParams.set('variant', String(variantId));
      return url.toString();
    } catch (error) {
      return baseUrl;
    }
  }

  function getWishlistStore() {
    return window.OatmealWishlistStore || null;
  }

  function getQuickAddWishlistMeta(button) {
    if (!button) return null;

    const meta = {
      handle: button.getAttribute('data-product-handle') || '',
      color: button.getAttribute('data-color') || '',
      productId: button.getAttribute('data-product-id') || '',
      variantId: button.getAttribute('data-variant-id') || '',
      productUrl: button.getAttribute('data-product-url') || '',
    };

    if (!meta.handle && !meta.color && !meta.productId && !meta.variantId) return null;
    return meta;
  }

  function syncQuickAddFavButton(button) {
    const store = getWishlistStore();
    if (!button || !store) return;

    const meta = getQuickAddWishlistMeta(button);
    if (!meta) return;

    store.register?.(meta);
    const pressed = store.has(meta);
    button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    button.setAttribute('aria-label', pressed ? 'Remove from wishlist' : 'Add to wishlist');
    button.classList.toggle('is-active', pressed);
  }

  function updateUtilityActions(modalContent) {
    const variantId = getSelectedVariantId(modalContent);
    const favBtn = modalContent.querySelector('[data-oat-qa-fav]');
    const shareBtn = modalContent.querySelector('[data-oat-qa-share]');

    if (favBtn) {
      favBtn.setAttribute('data-variant-id', variantId || '');
      const selectedColorBtn = modalContent.querySelector('.oat-qa-color-swatch.selected, .oat-qa-color-swatch.is-selected');
      const selectedColor = selectedColorBtn?.getAttribute('data-color') || '';
      favBtn.setAttribute('data-color', selectedColor);
      syncQuickAddFavButton(favBtn);
    }

    if (shareBtn) {
      shareBtn.setAttribute('data-variant-id', variantId || '');
    }
  }

  function toggleQuickAddWishlist(modalContent) {
    const favBtn = modalContent.querySelector('[data-oat-qa-fav]');
    const store = getWishlistStore();
    if (!favBtn || !store) return;

    const meta = getQuickAddWishlistMeta(favBtn);
    if (!meta) return;

    if (window.OATMEAL_IS_LOGGED_IN !== true) {
      window.OatmealAuthPromptOpen?.({ wishlistItem: meta });
      return;
    }

    store.register?.(meta);
    const next = !store.has(meta);
    if (next) store.add(meta);
    else store.remove(meta);

    syncQuickAddFavButton(favBtn);
  }

  async function handleQuickAddShare(modalContent) {
    const shareBtn = modalContent.querySelector('[data-oat-qa-share]');
    if (!shareBtn) return;

    const url = getShareUrl(modalContent);
    const title =
      modalContent.querySelector('.oat-qa-title')?.textContent?.trim() ||
      document.title;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        showQuickAddToast(modalContent, 'Link copied');
      } else {
        showQuickAddToast(modalContent, 'Could not share right now.');
        return;
      }
      shareBtn.classList.add('is-active');
      window.setTimeout(() => shareBtn.classList.remove('is-active'), 1200);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      showQuickAddToast(modalContent, 'Could not share right now.');
    }
  }

  function updateAddButton(modalContent) {
    const colorSelected = modalContent.querySelector('.oat-qa-color-swatch.selected');
    const sizeSelected = modalContent.querySelector('.oat-qa-size-buttons input[type="radio"]:checked');
    const addBtn = modalContent.querySelector('[data-add-to-cart]');
    if (addBtn) {
      const isReady = !!(colorSelected && sizeSelected);
      addBtn.disabled = !isReady;
      addBtn.classList.toggle('is-ready', isReady);
      if (isReady) {
        addBtn.classList.remove('is-loading', 'is-success');
        const textEl = addBtn.querySelector('.oat-qa-add-btn-text');
        if (textEl) textEl.textContent = 'Add to Bowl';
      }
    }
  }

  function getQuantityInput(modalContent) {
    return modalContent.querySelector(
      'product-form-component input[name="quantity"], product-form-component .quantity__input, product-form-component [name="quantity"]'
    );
  }

  function syncQuantityToForm(modalContent, quantity) {
    const nextQuantity = Math.max(1, Number(quantity) || 1);
    const valueEl = modalContent.querySelector('[data-oat-qty-value]');
    if (valueEl) valueEl.textContent = String(nextQuantity);

    const quantityInput = getQuantityInput(modalContent);
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = String(nextQuantity);
      quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function updateQuantity(modalContent, delta) {
    const valueEl = modalContent.querySelector('[data-oat-qty-value]');
    const current = Number(valueEl?.textContent || 1) || 1;
    const next = Math.max(1, current + delta);
    syncQuantityToForm(modalContent, next);
  }

  async function addVariantToCart(variantId, quantity) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: Number(variantId),
        quantity: Math.max(1, Number(quantity) || 1),
        properties: {
          '_Oatmeal Sale Type': 'Non - VTON',
        },
      }),
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok || json?.status === 422) {
      throw new Error(json?.description || json?.message || 'Could not add this item to your bowl');
    }

    return json;
  }

  async function fetchCartCount() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) return null;
    const cart = await response.json().catch(() => null);
    return Number.isFinite(Number(cart?.item_count)) ? Number(cart.item_count) : null;
  }

  function syncHeaderCartCount(count) {
    if (!Number.isFinite(count)) return;

    const drawer = document.getElementById('header-cart-drawer');
    if (drawer) {
      drawer.dataset.cartItemCount = String(count);
    }

    document.querySelectorAll('.flo-cart-icon-bubble, [data-testid="cart-bubble"]').forEach((el) => {
      el.textContent = String(count);
      el.classList.toggle('hidden', count === 0);
    });

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count === 0);
    });
  }

  async function handleAddToCart(modalContent) {
    const sizeInput = modalContent.querySelector('.oat-qa-size-buttons input[type="radio"]:checked');
    if (!sizeInput) {
      showQuickAddToast(modalContent, 'Please select a size first.');
      return;
    }

    const variantId = sizeInput.dataset.variantId;
    if (!variantId) {
      showQuickAddToast(modalContent, 'This size or colour is not available right now.');
      return;
    }

    const addBtn = modalContent.querySelector('[data-add-to-cart]');
    const dialogComponent = getQuickAddDialog();
    const quantity = Number(modalContent.querySelector('[data-oat-qty-value]')?.textContent || 1) || 1;

    if (addBtn) {
      addBtn.disabled = true;
      const textEl = addBtn.querySelector('.oat-qa-add-btn-text');
      if (textEl) textEl.textContent = 'Add to Bowl';
      addBtn.classList.remove('is-ready', 'is-success');
      addBtn.classList.add('is-loading');
    }

    try {
      await addVariantToCart(variantId, quantity);

      const count = await fetchCartCount();
      if (Number.isFinite(count)) syncHeaderCartCount(count);

      if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
        dialogComponent.closeDialog();
      }

      window.setTimeout(() => {
        window.OatShopflo?.openCart();
      }, 80);
    } catch (error) {
      console.error('[Oatmeal QA] Add to cart failed', error);
      showQuickAddToast(modalContent, error?.message || 'Could not add this item to your bowl');
      if (addBtn) {
        addBtn.disabled = false;
        const textEl = addBtn.querySelector('.oat-qa-add-btn-text');
        if (textEl) textEl.textContent = 'Add to Bowl';
        addBtn.classList.remove('is-loading', 'is-success');
      }
      return;
    } finally {
      if (addBtn) {
        addBtn.disabled = false;
        const textEl = addBtn.querySelector('.oat-qa-add-btn-text');
        if (textEl) textEl.textContent = 'Add to Bowl';
        addBtn.classList.remove('is-loading', 'is-success');
        updateAddButton(modalContent);
      }
    }
  }

  function openSizeChartDialog(imgUrl) {
    if (!imgUrl) return false;

    const existing = document.getElementById('oat-size-chart-dialog');
    if (existing) existing.remove();

    const hasOpenQuickAdd = !!document.querySelector(
      '#quick-add-dialog dialog[open], dialog.quick-add-modal[open]:not(.oat-size-chart-dialog)'
    );

    const dialog = document.createElement('dialog');
    dialog.id = 'oat-size-chart-dialog';
    dialog.className = 'quick-add-modal dialog-modal oat-size-chart-dialog';
    if (hasOpenQuickAdd) {
      dialog.classList.add('oat-size-chart-dialog--nested');
      dialog.style.setProperty('--backdrop-opacity', '0');
    }

    dialog.innerHTML = `
      <div class="dialog-modal__content">
        <div class="oat-size-chart">
          <header class="oat-size-chart__header">
            <div class="oat-size-chart__title">Size chart</div>
            <div class="oat-size-chart__viewer-toolbar">
              <button class="oat-size-chart__viewer-action" type="button" data-size-chart-zoom-out aria-label="Zoom out">−</button>
              <button class="oat-size-chart__viewer-action" type="button" data-size-chart-zoom-in aria-label="Zoom in">+</button>
              <button class="oat-size-chart__viewer-close oat-size-chart__close" type="button" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </header>
          <div class="oat-size-chart__body">
            <div class="oat-size-chart__viewer-stage" data-size-chart-stage>
              <img class="oat-size-chart__img" data-size-chart-img src="${imgUrl}" alt="Size chart" loading="eager">
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(dialog);

    dialog.querySelector('.oat-size-chart__close')?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      dialog.close();
    });

    dialog.addEventListener('click', (e) => {
      const rect = dialog.getBoundingClientRect();
      const inDialog =
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom;
      if (!inDialog) dialog.close();
    });

    dialog.addEventListener('close', () => dialog.remove());

    setupSizeChartZoom(dialog);
    dialog.showModal();
    return true;
  }

  function setupSizeChartZoom(dialog) {
    const stage = dialog.querySelector('[data-size-chart-stage]');
    const img = dialog.querySelector('[data-size-chart-img]');
    const zoomInBtn = dialog.querySelector('[data-size-chart-zoom-in]');
    const zoomOutBtn = dialog.querySelector('[data-size-chart-zoom-out]');
    if (!stage || !img || !zoomInBtn || !zoomOutBtn) return;

    const state = {
      scale: 1,
      x: 0,
      y: 0,
      baseWidth: 0,
      baseHeight: 0,
      pointers: new Map(),
      dragOriginX: 0,
      dragOriginY: 0,
      pinchDistance: 0,
      pinchScale: 1
    };

    function clamp(num, min, max) {
      return Math.min(max, Math.max(min, num));
    }

    function resetTransform() {
      state.scale = 1;
      state.x = 0;
      state.y = 0;
      stage.classList.remove('is-dragging');
      img.classList.remove('is-zoomed');
      img.style.transform = 'translate(0px, 0px) scale(1)';
      zoomOutBtn.disabled = true;
      zoomInBtn.disabled = false;
    }

    function getBaseSize() {
      if (!state.baseWidth || !state.baseHeight) return null;
      return { width: state.baseWidth, height: state.baseHeight };
    }

    function fitImageToStage() {
      const stageRect = stage.getBoundingClientRect();
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      if (!stageRect.width || !stageRect.height || !naturalWidth || !naturalHeight) return;

      const stageRatio = stageRect.width / stageRect.height;
      const imageRatio = naturalWidth / naturalHeight;
      let width;
      let height;

      if (imageRatio > stageRatio) {
        width = stageRect.width;
        height = width / imageRatio;
      } else {
        height = stageRect.height;
        width = height * imageRatio;
      }

      state.baseWidth = width;
      state.baseHeight = height;
      img.style.width = Math.max(1, Math.floor(width)) + 'px';
      img.style.height = Math.max(1, Math.floor(height)) + 'px';
    }

    function clampPan() {
      if (state.scale <= 1) {
        state.x = 0;
        state.y = 0;
        return;
      }

      const base = getBaseSize();
      if (!base) return;

      const maxX = Math.max(0, (base.width * state.scale - base.width) / 2);
      const maxY = Math.max(0, (base.height * state.scale - base.height) / 2);
      state.x = clamp(state.x, -maxX, maxX);
      state.y = clamp(state.y, -maxY, maxY);
    }

    function applyTransform() {
      clampPan();
      img.classList.toggle('is-zoomed', state.scale > 1);
      img.style.transform =
        'translate(' + Math.round(state.x) + 'px, ' + Math.round(state.y) + 'px) scale(' + state.scale.toFixed(3) + ')';
      zoomOutBtn.disabled = state.scale <= 1;
      zoomInBtn.disabled = state.scale >= 4;
    }

    function setScale(nextScale) {
      state.scale = clamp(nextScale, 1, 4);
      if (state.scale <= 1) {
        state.x = 0;
        state.y = 0;
        stage.classList.remove('is-dragging');
      }
      applyTransform();
    }

    function zoom(direction) {
      const factor = direction > 0 ? 1.2 : 1 / 1.2;
      setScale(state.scale * factor);
    }

    function getPointerDistance(points) {
      const [a, b] = points;
      if (!a || !b) return 0;
      return Math.hypot(a.x - b.x, a.y - b.y);
    }

    function releasePointer(event) {
      if (!state.pointers.has(event.pointerId)) return;
      state.pointers.delete(event.pointerId);

      if (state.pointers.size < 2) {
        state.pinchDistance = 0;
      }

      if (state.pointers.size === 1 && state.scale > 1) {
        const remaining = Array.from(state.pointers.values())[0];
        state.dragOriginX = remaining.x - state.x;
        state.dragOriginY = remaining.y - state.y;
        stage.classList.add('is-dragging');
        return;
      }

      stage.classList.remove('is-dragging');
    }

    zoomInBtn.addEventListener('click', () => zoom(1));
    zoomOutBtn.addEventListener('click', () => zoom(-1));

    stage.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        zoom(event.deltaY < 0 ? 1 : -1);
      },
      { passive: false }
    );

    stage.addEventListener('pointerdown', (event) => {
      stage.setPointerCapture?.(event.pointerId);
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (state.pointers.size === 1 && state.scale > 1) {
        state.dragOriginX = event.clientX - state.x;
        state.dragOriginY = event.clientY - state.y;
        stage.classList.add('is-dragging');
      }

      if (state.pointers.size === 2) {
        state.pinchDistance = getPointerDistance(Array.from(state.pointers.values()));
        state.pinchScale = state.scale;
        stage.classList.remove('is-dragging');
      }
    });

    stage.addEventListener('pointermove', (event) => {
      if (!state.pointers.has(event.pointerId)) return;
      state.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (state.pointers.size === 2) {
        const distance = getPointerDistance(Array.from(state.pointers.values()));
        if (state.pinchDistance > 0) {
          setScale(state.pinchScale * (distance / state.pinchDistance));
        }
        return;
      }

      if (state.pointers.size === 1 && state.scale > 1) {
        state.x = event.clientX - state.dragOriginX;
        state.y = event.clientY - state.dragOriginY;
        applyTransform();
      }
    });

    stage.addEventListener('pointerup', releasePointer);
    stage.addEventListener('pointercancel', releasePointer);
    stage.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') releasePointer(event);
    });

    img.addEventListener('dblclick', () => {
      if (state.scale > 1) {
        resetTransform();
      } else {
        setScale(2);
      }
    });

    const handleResize = () => {
      fitImageToStage();
      resetTransform();
    };

    window.addEventListener('resize', handleResize);
    dialog.addEventListener('close', () => window.removeEventListener('resize', handleResize), { once: true });

    if (img.complete) {
      fitImageToStage();
      resetTransform();
    } else {
      img.addEventListener('load', () => {
        fitImageToStage();
        resetTransform();
      }, { once: true });
    }
  }

  window.OatmealOpenSizeChartDialog = openSizeChartDialog;

  function toggleFav(button) {
    const icon = button.querySelector('.icon-fav, .icon-fav-selected');
    if (!icon) return;

    const isSelected = button.getAttribute('aria-pressed') === 'true';

    if (isSelected) {
      icon.classList.remove('icon-fav-selected');
      icon.classList.add('icon-fav');
      button.setAttribute('aria-pressed', 'false');
    } else {
      icon.classList.remove('icon-fav');
      icon.classList.add('icon-fav-selected');
      button.setAttribute('aria-pressed', 'true');
    }
  }

  function toggleWishlist(button) {
    const isPressed = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', isPressed ? 'false' : 'true');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      patchQuickAdd();
    });
  } else {
    patchQuickAdd();
  }

  function getQuickAddContext(trigger) {
    const card = trigger.closest('.card, .product-card, [data-product-card], li, article');
    if (!card) return null;

    const quickAdd = card.querySelector('quick-add-component');
    if (!quickAdd) return null;

    return { card, quickAdd };
  }

  function syncPinnedVariant(trigger, card, quickAdd) {
    const pinnedVariantId =
      getSelectedVariantIdFromCard(card) ||
      card.dataset.variantId ||
      card.getAttribute('data-variant-id') ||
      trigger.getAttribute('data-variant-id') ||
      null;

    if (!pinnedVariantId) return;

    quickAdd.dataset.selectedVariantId = String(pinnedVariantId);
    quickAdd.dataset.variantId = String(pinnedVariantId);
    quickAdd.dataset.currentVariantId = String(pinnedVariantId);
  }

  function setQuickAddTriggerLoading(trigger, isLoading) {
    if (!(trigger instanceof HTMLElement)) return;

    trigger.classList.toggle('is-opening', isLoading);
    trigger.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (isLoading) {
      trigger.setAttribute('disabled', '');
    } else {
      trigger.removeAttribute('disabled');
    }
  }

  document.addEventListener('pointerenter', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const trigger = target.closest('[data-oat-quickadd-trigger]');
    if (!trigger) return;

    const context = getQuickAddContext(trigger);
    if (!context) return;

    syncPinnedVariant(trigger, context.card, context.quickAdd);
    context.quickAdd.oatPrefetchQuickAdd?.();
  }, true);

  document.addEventListener('touchstart', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const trigger = target.closest('[data-oat-quickadd-trigger]');
    if (!trigger) return;

    const context = getQuickAddContext(trigger);
    if (!context) return;

    syncPinnedVariant(trigger, context.card, context.quickAdd);
    context.quickAdd.oatPrefetchQuickAdd?.();
  }, { passive: true, capture: true });

  // Tile overlay quick-add trigger -> open theme quick add with prefetch support
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const trigger = target.closest('[data-oat-quickadd-trigger]');
    if (!trigger) return;

    e.preventDefault();
    e.stopPropagation();

    const context = getQuickAddContext(trigger);
    if (!context) return;

    syncPinnedVariant(trigger, context.card, context.quickAdd);

    setQuickAddTriggerLoading(trigger, true);

    Promise.resolve(context.quickAdd.oatOpenQuickAdd?.())
      .catch((error) => {
        console.error('[Oatmeal QA] Quick add open failed', error);
      })
      .finally(() => {
        setQuickAddTriggerLoading(trigger, false);
      });
  });

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const favBtn = target.closest('[data-oat-wishlist]');
    if (!favBtn) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    toggleWishlist(favBtn);
  });

})();

(function () {
  'use strict';

  const drawer = document.getElementById('oat-studio-drawer');
  if (!drawer) return;

  const content = drawer.querySelector('[data-studio-drawer-content]');
  const sourceWrap = document.getElementById('oat-studio-source');
  if (!content || !sourceWrap) return;

  let mountedStudio = null;

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
  }

  function getStudioNode() {
    return sourceWrap.querySelector('.oatmeal-studio[data-section-id]');
  }

  function parseStudioProductPayload(raw) {
    const value = String(raw || '').trim();
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn('[STUDIO] invalid product payload', error);
      return null;
    }
  }

  function openStudioDrawer(preselect) {
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);

    content.innerHTML = '';
    const studio = getStudioNode();
    if (!studio) {
      console.error('[STUDIO] source markup missing');
      return;
    }

    // MOVE the real node (do NOT clone)
    content.innerHTML = '';
    content.appendChild(studio);
    mountedStudio = studio;

    const detail =
      preselect && typeof preselect === 'object'
        ? { reset: true, ...preselect }
        : { variantId: preselect, reset: true };

    mountedStudio.dataset.preselectedVariant = detail.variantId ? String(detail.variantId) : '';
    mountedStudio.dataset.preselectedProductId = detail.productId ? String(detail.productId) : '';

    // If studio is already mounted (it is), call the live hook
    if (mountedStudio.__oatStudioApplyPreselect) {
      mountedStudio.__oatStudioApplyPreselect(detail);
    } else {
      // fallback (in case mount hasn't run yet)
      document.dispatchEvent(new CustomEvent('oatmeal:selectVariant', {
        detail
      }));
    }
  }

  function closeStudioDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    lockScroll(false);

    if (mountedStudio && mountedStudio.__oatStudioClear) mountedStudio.__oatStudioClear();
    // MOVE it back to hidden source so it stays alive
    if (mountedStudio) {
      sourceWrap.appendChild(mountedStudio);
      mountedStudio = null;
    }

    content.innerHTML = '';
  }

  function openStudioFromTrigger(openBtn) {
    const variantId =
      openBtn.getAttribute('data-variant-id') ||
      openBtn.closest('[data-variant-id]')?.getAttribute('data-variant-id') ||
      null;
    const productId =
      openBtn.getAttribute('data-product-id') ||
      openBtn.closest('[data-product-id]')?.getAttribute('data-product-id') ||
      null;
    const productPayload =
      parseStudioProductPayload(openBtn.getAttribute('data-studio-product')) ||
      parseStudioProductPayload(openBtn.closest('[data-studio-product]')?.getAttribute('data-studio-product')) ||
      null;
    const category =
      openBtn.getAttribute('data-studio-category') ||
      openBtn.closest('[data-studio-category]')?.getAttribute('data-studio-category') ||
      null;

    openStudioDrawer({
      variantId,
      productId,
      category,
      product: productPayload
    });
  }

  document.addEventListener('oatmeal:openStudio', (e) => {
    const trigger = e.detail?.trigger;
    if (trigger instanceof Element) openStudioFromTrigger(trigger);
  });

  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;

    const openBtn = target.closest('[data-oat-tryon],[data-open-studio]');
    if (openBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      openStudioFromTrigger(openBtn);
      return;
    }

    const closeBtn = target.closest('[data-studio-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      closeStudioDrawer();
    }
  }, true);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
      closeStudioDrawer();
    }
  });
})();
